import { useState, useReducer, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GridColDef,
    GridActionsCellItem,
    GridPaginationModel,
    GridColumnVisibilityModel,
    GridSortModel,
    GridRenderCellParams,
} from '@mui/x-data-grid';
import localforage from 'localforage';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import BaseCard from '@/components/ui/BaseCard';
import SearchBar from '@/components/form/SearchBar';
import BaseDataTable from '@/components/ui/BaseDataTable';
import { StoresPageState, StoresPageAction, Store as StoreType, StoreLocation, StoreCluster } from '@/types';
import { Store, LocationOn, Dashboard } from '@mui/icons-material';
import { Chip, Box } from '@mui/material';
import { fetchStoreListAPI } from '@/api/stores';
import { useAppContext } from '@/provider/contexts';
import '@/styles/pages/Stores/StoresPage.scss';
import { getInputMinLengthLimit } from '@/utils/inputUtils';
import { useTranslation } from 'react-i18next';
import DeviceTableColumn from '@/components/ui/BaseTableColumn';

function reducer(state: StoresPageState, action: StoresPageAction) {
    switch (action.type) {
        case 'SET_TABLE_LOADING':
            return { ...state, tableLoading: action.payload };
        case 'SET_SEARCH_QUERY':
            return {
                ...state,
                searchQuery: action.payload,
            };
        case 'SET_PAGINATION_MODEL':
            return {
                ...state,
                paginationModel: action.payload,
            };
        case 'SET_STORE_TABLE':
            return {
                ...state,
                tableLoading: false,
                storeList: action.payload.aaData,
                rowCount: action.payload.total_records,
            };
        case 'SET_SORT_MODEL':
            return {
                ...state,
                sortModel: action.payload,
            };
        default:
            return state;
    }
}

function StoresPage() {
    const navigate = useNavigate();
    const { userDetails, corporationDetails, setManageStoreId, setNotification } = useAppContext();
    const [validatedMessage, setValidatedMessage] = useState<{ [key: string]: string | null }>({});
    const { t, i18n } = useTranslation();

    const defaultState: StoresPageState = {
        tableLoading: false,
        storeList: [],
        rowCount: 0,
        searchQuery: '',
        paginationModel: {
            page: 0,
            pageSize: 5,
        },
        sortModel: { field: null, sort: null },
    };

    const [{ tableLoading, storeList, rowCount, searchQuery, paginationModel, sortModel }, dispatch] = useReducer(
        reducer,
        defaultState,
    );
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    const getRowId = (row: StoreType) => {
        return row.store_id;
    };

    const openStoreMap = (storeLocation: StoreLocation) => {
        if (!storeLocation) return;
        window.open(
            Array.isArray(storeLocation)
                ? `https://www.google.com/maps/search/?api=1&query=${storeLocation[0]},${storeLocation[1]}`
                : `https://www.google.com/maps/search/?api=1&query=${storeLocation.latitude},${storeLocation.longitude}`,
            '_blank',
        );
    };
    const hasStoreOverviewPermission = ['*', 'page-storeOverview-all-read'].some((e) =>
        corporationDetails.user_abilities.includes(e),
    );
    const canSeeAction = (userDetails?.user_level ?? 0) >= 2 || hasStoreOverviewPermission;

    const renderSimpleCell = useCallback((params: GridRenderCellParams) => {
        const value = params.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <DeviceTableColumn columnValue={displayValue} />;
    }, []);

    const columns = useMemo<GridColDef[]>(() => {
        const base: GridColDef[] = [
            { field: 'store_id', headerName: t('Store ID'), flex: 1, renderCell: renderSimpleCell },
            { field: 'store_code', headerName: t('Store Code'), flex: 1, renderCell: renderSimpleCell },
            {
                field: 'store_name',
                headerName: t('Store Name'),
                flex: 1.5,
                minWidth: 100,
                renderCell: renderSimpleCell,
            },
            {
                field: 'distributor_name',
                headerName: t('Distributor Name'),
                flex: 1,
                minWidth: 100,
                renderCell: renderSimpleCell,
            },
            { field: 'zip_code', headerName: t('Zip code'), flex: 1, renderCell: renderSimpleCell },
            { field: 'store_address', headerName: t('Address'), flex: 1, renderCell: renderSimpleCell },
            {
                field: 'store_cluster',
                headerName: t('Cluster'),
                flex: 1,
                minWidth: 100,
                sortable: false,
                renderCell: (params) => {
                    if (!params.value?.length) return <DeviceTableColumn columnValue="-" />;
                    return (
                        <DeviceTableColumn>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                    height: '100%',
                                }}
                            >
                                {params.value.map((cluster: StoreCluster) => (
                                    <Chip key={cluster.id} label={cluster.cluster_name} size="small" sx={{ mr: 0.2 }} />
                                ))}
                            </Box>
                        </DeviceTableColumn>
                    );
                },
            },
            {
                field: 'store_location',
                type: 'actions',
                headerName: t('Location'),
                flex: 1,
                minWidth: 140,
                disableColumnMenu: false,
                getActions: ({ row }) => [
                    row.store_location ? (
                        <GridActionsCellItem
                            icon={<LocationOn />}
                            label={t('Location')}
                            sx={{ color: 'primary.main' }}
                            onClick={() => openStoreMap(row.store_location)}
                        />
                    ) : (
                        <></>
                    ),
                ],
            },
        ];

        if (canSeeAction) {
            base.push({
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                flex: 1,
                minWidth: 140,
                disableColumnMenu: false,
                getActions: ({ row }) => [
                    <GridActionsCellItem
                        icon={<Dashboard />}
                        label={t('To Store Dashboard')}
                        sx={{ color: 'primary.main' }}
                        onClick={() => navigateStoreOverview(row.store_id)}
                    />,
                ],
            });
        }

        return base;
        // depend on language + permission so it recomputes on change
    }, [t, i18n.language, canSeeAction, renderSimpleCell]);

    const sortModelChangeHandler = (sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];
            dispatch({
                type: 'SET_SORT_MODEL',
                payload: { field: field as keyof StoreType, sort: sort as 'asc' | 'desc' },
            });
        } else {
            dispatch({
                type: 'SET_SORT_MODEL',
                payload: { field: null, sort: null },
            });
        }
    };

    // server side pagination
    const fetchStoreListHandler = async () => {
        fetchAbortControllerRef.current?.abort();
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        dispatch({ type: 'SET_TABLE_LOADING', payload: true });

        const corporationId = await localforage.getItem('corporation_id');
        const { field, sort } = sortModel;

        const params = {
            cid: corporationId as number,
            keyword: searchQuery,
            current_page: paginationModel.page + 1,
            data_per_page: paginationModel.pageSize,
            sort_field: field,
            sort_order: sort,
        };

        try {
            const {
                data: { data, message, isSuccess },
            } = await fetchStoreListAPI(params, { signal: abortController.signal });

            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;

            if (isSuccess) {
                dispatch({ type: 'SET_STORE_TABLE', payload: data });
                setValidatedMessage({});
            } else if (message === 'Validation failed.' && Array.isArray(data)) {
                const validatedMessageObj: { [key: string]: string | null } = {};
                data.forEach(({ field, message }) => {
                    validatedMessageObj[field] = message;
                });
                setValidatedMessage(validatedMessageObj);
                setNotification({ message, severity: 'error' });
            } else {
                setNotification({ message, severity: 'error' });
            }
        } catch (error) {
            if (abortController.signal.aborted) return;
            setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
        } finally {
            if (latestFetchIdRef.current === fetchId) {
                dispatch({ type: 'SET_TABLE_LOADING', payload: false });
                fetchAbortControllerRef.current = null;
            }
        }
    };

    const pageChangeHandler = (pageModel: GridPaginationModel) => {
        if (paginationModel.pageSize !== pageModel.pageSize) {
            // When the table page size changes, reset the page to 1.
            dispatch({
                type: 'SET_PAGINATION_MODEL',
                payload: { ...pageModel, page: 0 },
            });
        } else {
            dispatch({ type: 'SET_PAGINATION_MODEL', payload: pageModel });
        }
    };

    const navigateStoreOverview = (storeId: number) => {
        setManageStoreId(storeId);
        navigate(`/store_management/overview`);
    };

    const searchHandler = (value: string) => {
        const minLength = getInputMinLengthLimit(value);

        if (value.trim().length === 0 || value.length >= minLength) {
            setValidatedMessage({});
            dispatch({ type: 'SET_SEARCH_QUERY', payload: value });
        } else {
            setValidatedMessage({ keyword: t('Keyword length validation', { count: minLength }) });
        }
    };

    useEffect(() => {
        fetchStoreListHandler();
    }, [paginationModel, searchQuery, sortModel]);

    useEffect(() => {
        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        store_id: true,
        store_code: true,
        store_name: true,
        store_address: true,
        distributor_name: false,
        zip_code: false,
        store_cluster: true,
        store_location: false,
        action: true,
    });

    return (
        <>
            <BasePaper className="store-page">
                <BaseTitle icon={<Store />} title={t('Stores')} />

                <BaseCard className="store-list-card">
                    <div className="card-header">
                        <SearchBar
                            onSearch={searchHandler}
                            error={!!validatedMessage.keyword}
                            helperText={validatedMessage.keyword as string}
                            sx={{ my: 2 }}
                        ></SearchBar>
                    </div>
                    <BaseDataTable
                        rows={storeList}
                        columns={columns}
                        getRowId={getRowId}
                        loading={tableLoading}
                        paginationModel={paginationModel}
                        paginationMode="server"
                        rowCount={rowCount}
                        onPaginationModelChange={pageChangeHandler}
                        sortingMode="server"
                        onSortModelChange={sortModelChangeHandler}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={setColumnVisibilityModel}
                    />
                </BaseCard>
            </BasePaper>
        </>
    );
}

export default StoresPage;
